//Feb26 FormSaveContext saving form details correctly to Firestone
import React, { createContext, useContext, useMemo } from 'react';
import { useFormContext } from './FormContext';
import { useFormTheme } from './FormThemeContext'; // Commented out for testing
import { useFormLayout } from './FormLayoutContext'; // Commented out for testing
import { useDropdownDependencies } from './DropdownDependenciesContext'; // NEW: Import dependencies context
import { useFormSubmissionSettings } from './FormSubmissionSettingsContext';

// NEW: read canonical in-memory metadata/options (avoids legacy storage keys)
import { useFieldMetadata } from './FieldMetadataContext';
import { useMultiAnswerField } from './MultiAnswerFieldContext';
import { useFormListsDraftSync } from './FormListsDraftSyncContext';

import {
  cleanDropdownDependenciesForOptions,
} from '../utils/formListsDependencyCleanupUtils';

import { auth, functions } from '../firebase'; // Import Firebase
import { httpsCallable } from 'firebase/functions';
import { compressDataForDraft, saveDraftFormToFirestore } from '../utils/saveDraftFormToFirestore'; // Using compression now
import { useAuth } from './AuthContext'; // Import useAuth to access organizationId
import { safeGetItem } from '../utils/storageUtils';
import {
  syncStagedFormIntegrationConfigs,
} from '../utils/formIntegrationConfigSync';
import {
  ONBOARDING_EVENTS,
  ONBOARDING_EVENT_SOURCES,
} from '../utils/onboardingLifecycle';

// Utility to selectively serialize layout configuration
const serializeLayoutConfig = (layoutConfig) => {
  const seen = new WeakSet();
  return JSON.stringify(layoutConfig, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return; // Skip circular references
      }
      seen.add(value);

      // Only serialize essential properties necessary for UI construction
      if (value && typeof value === 'object' && value.id && value.type) {
        return {
          id: value.id,
          type: value.type,
          label: value.label,  // Often used for display
          placeholder: value.placeholder,  // For inputs
          required: value.required,  // For validating inputs
          index: value.index,  // For order or specific placements
          ufi: value.ufi,  // Include the UFI to ensure field identity is maintained
          // Include other properties as needed based on your component's usage
        };
      }
    }
    return value;
  });
};

const FormSaveContext = createContext();

export const FormSaveProvider = ({ children }) => {
  // Only accessing FormContext for testing
  const { formId, formDetails, formTitle, formDescription } = useFormContext();
  const { organizationId } = useAuth(); // Get organizationId from AuthContext
  const { colors, fonts, submitButtonStyle, fieldVariant, selectedTheme } = useFormTheme(); // Get theme data
  const { layoutConfig } = useFormLayout(); // Get layoutConfig from FormLayoutContext
  const { dropdownDependencies = {} } = useDropdownDependencies() || {};
  const { submissionSettings } = useFormSubmissionSettings(); 
  //  Canonical in-memory stores (avoids legacy storage reads)
  const { fieldsMetadata: fieldsMetadataFromCtx } = useFieldMetadata() || {};
  const { dropdownOptions: dropdownOptionsFromCtx } = useMultiAnswerField() || {};
  const {
    flushFormListsDraftChanges,
    clearFormListsUnsavedWorkingCopy,
  } = useFormListsDraftSync() || {};

// match FormLayoutContext policy for choosing builder view mode
const isBuilderRoute = useMemo(() => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/form-builder');
}, []);

const builderViewMode = useMemo(() => {
  try {
    if (!formId) return 'auto';
    const raw = sessionStorage.getItem(`form_${formId}_layoutVersionMode`);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.value || 'auto'; // 'auto' | 'published' | 'draftOverlay'
  } catch (e) {
    return 'auto';
  }
}, [formId]);

const effectiveViewMode = useMemo(() => {
  // Only builder honors the toggle; everything else is published.
  if (isBuilderRoute) return builderViewMode;
  return 'published';
}, [isBuilderRoute, builderViewMode]);

  /**
   * Records form-save progress after the draft/overlay write succeeds.
   *
   * Enterprise rules:
   * - Do not block save UX if lifecycle recording fails.
   * - Do not mark activation here; backend reducer owns state transition.
   * - Use a per-save eventId so the audit trail can capture repeated saves.
   * - Keep Firestore onboarding state owned by recordOnboardingEvent.
   */
  const recordSaveOnboardingEvent = async ({
    currentUser,
    saveMode = 'draftOverlay',
    saveResponse = null,
  } = {}) => {
    if (!currentUser?.uid) {
      console.log(
        '[FormSaveContext] Skipping save onboarding event because ' +
        'no authenticated user is present.'
      );
      return null;
    }

    if (!formId) {
      console.warn(
        '[FormSaveContext] Skipping save onboarding event because ' +
        'formId is missing.'
      );
      return null;
    }

    try {
      const recordOnboardingEvent = httpsCallable(
        functions,
        'recordOnboardingEvent'
      );

      const eventId =
        `form_save_${currentUser.uid}_${formId}_${Date.now()}`;

      const response = await recordOnboardingEvent({
        eventName: ONBOARDING_EVENTS.CAMPAIGN_URL_FORM_SAVED,
        eventId,
        insertId: eventId,
        eventSource: ONBOARDING_EVENT_SOURCES.FORM_SAVE_CONTEXT,
        formId,
        eventProperties: {
          formId,
        },
        metadata: {
          source: 'FormSaveContext',
          saveMode,
          effectiveViewMode,
          saveStatus: saveResponse?.status || null,
          saveMessage: saveResponse?.message || null,
        },
      });

      console.log('[FormSaveContext] Onboarding save event recorded:', {
        eventId,
        result: response?.data || null,
      });

      return response?.data || null;
    } catch (eventErr) {
      console.warn(
        '[FormSaveContext] Onboarding save event failed (non-fatal):',
        {
          formId,
          error: eventErr?.message || eventErr,
        }
      );

      return null;
    }
  };



  const saveFormAsDraft = async () => {
    console.log('[FormSaveContext] Starting to save form as a draft...');
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error('[FormSaveContext] No authenticated user found. Cannot save the draft.');
      return;
    }

    // Log if the organizationId is missing or empty before saving
    if (!organizationId) {
      console.warn('[FormSaveContext] Warning: organizationId is missing or empty.');
    } else {
      console.log('[FormSaveContext] organizationId from useAuth:', organizationId);
    }

    // ** Add log to check if formDetails is undefined **
    if (!formDetails) {
      console.error('[FormSaveContext] formDetails is undefined! Please check useFormContext.');
      return;
    }

  if (!formTitle || !formDescription) {
    console.warn('[FormSaveContext] Warning: formTitle or formDescription is missing before constructing updatedFormDetails.');
  }

 // 🆕 Load ingest mapping (if your mapping UI saved it)
 // Shape example:
 // {
 //   canonicalNameUfi: "<ufi>",
 //   assetTypeUfi:     "<ufi>",
 //   statusUfi:        "<ufi>|null",
 //   tagUfis:          ["<ufi>", ...],
 //   collectionUfi:    "<ufi>|null"
 // }
 const storedAssetIngestConfig =
   safeGetItem(`assetIngestConfig_${formId}`) || null;
 console.log(
   '[FormSaveContext] Retrieved asset ingest config for saving:',
   storedAssetIngestConfig
 );

// ────────────────────────────────────────────────────────────────
// Sanitize base formDetails so "metadata" never includes blobs
// or overlay payloads. This prevents accidental overwrites and keeps
// a clean separation between:
//   • metadata (title, description, sharing, ownership, etc.)
//   • blobs (compressedData/layoutConfig/theme/...)
//   • working copy (draftOverlay)
// ────────────────────────────────────────────────────────────────
const {
  // strip working-copy payload + related index fields
  draftOverlay: _draftOverlay,
  draftOverlayStatus: _draftOverlayStatus,
  draftOverlaySavedAt: _draftOverlaySavedAt,
  draftOverlaySavedBy: _draftOverlaySavedBy,

  // strip compressed/root blob fields (these should not live in metadata)
  compressedData: _compressedDataBlob,
  layoutConfig: _layoutConfigBlob,
  theme: _themeBlob,
  fieldsMetadata: _fieldsMetadataBlob,
  dropdownOptions: _dropdownOptionsBlob,
  dropdownDependencies: _dropdownDependenciesBlob,
  submissionSettings: _submissionSettingsBlob,

  // keep the rest as "base metadata"
  ...baseFormDetails
} = formDetails || {};

// ADD: prove what we removed (safe summary only)
console.log('[FormSaveContext] baseFormDetails sanitized (removed overlay/blob keys):', {
  hadDraftOverlay: !!_draftOverlay,
  hadCompressedData: !!_compressedDataBlob,
  hadLayoutConfigBlob: !!_layoutConfigBlob,
  hadThemeBlob: !!_themeBlob,
  hadFieldsMetadataBlob: !!_fieldsMetadataBlob,
  hadDropdownOptionsBlob: !!_dropdownOptionsBlob,
  hadDropdownDependenciesBlob: !!_dropdownDependenciesBlob,
  hadSubmissionSettingsBlob: !!_submissionSettingsBlob,
});


// Ensure we use the latest title and description even if formDetails is stale
const updatedFormDetails = {
  ...baseFormDetails, //  Use sanitized metadata only
  title: formTitle, // Ensure latest title
  description: formDescription, // Ensure latest description
  createdBy: baseFormDetails?.createdBy || currentUser.uid,
  owner: baseFormDetails?.owner || currentUser.uid,
  organizationId: organizationId || '',
  // keep whatever was there – _do not_ force a default
  isTemplate      : baseFormDetails?.isTemplate,
  templateScope   : baseFormDetails?.templateScope,
  templateCategory: baseFormDetails?.templateCategory,
  isPublic        : baseFormDetails?.isPublic,
  orgAccess    : baseFormDetails?.orgAccess   ?? 'none',
  sharedWith   : baseFormDetails?.sharedWith  ?? {},
  lastModified: new Date(),
};



 // If a mapping exists, make the draft explicit: it's the Asset Upload Form
 if (storedAssetIngestConfig && typeof storedAssetIngestConfig === 'object') {
   updatedFormDetails.isAssetUploadForm = true;
   console.log('[FormSaveContext] Marked draft as Asset Upload Form (mapping present).');
 }

  if (formDetails?.isTemplate && updatedFormDetails.isTemplate === undefined) {
    updatedFormDetails.isTemplate = true;
  }
  
console.log('[FormSaveContext] updatedFormDetails after construction:', updatedFormDetails);
if (!updatedFormDetails.title || !updatedFormDetails.description) {
  console.error('[FormSaveContext] Error: updatedFormDetails is missing title or description!');
}

    // Collect all theme-related data
    // Prefer FormThemeContext (canonical)
    // Fallback to mode-aware storage if theme context isn't hydrated yet
    const storedThemeSelected =
      safeGetItem(`form_${formId}_selectedTheme__${effectiveViewMode}`) ||
      safeGetItem(`form_${formId}_selectedTheme__published`) ||
      safeGetItem(`form_${formId}_selectedTheme`);

    const storedThemeColors =
      safeGetItem(`form_${formId}_colors__${effectiveViewMode}`) ||
      safeGetItem(`form_${formId}_colors__published`) ||
      safeGetItem(`form_${formId}_colors`);

    const storedThemeFonts =
      safeGetItem(`form_${formId}_fonts__${effectiveViewMode}`) ||
      safeGetItem(`form_${formId}_fonts__published`) ||
      safeGetItem(`form_${formId}_fonts`);

    const storedThemeButton =
      safeGetItem(`form_${formId}_submitButtonStyle__${effectiveViewMode}`) ||
      safeGetItem(`form_${formId}_submitButtonStyle__published`) ||
      safeGetItem(`form_${formId}_submitButtonStyle`);

    const storedThemeVariant =
      safeGetItem(`form_${formId}_fieldVariant__${effectiveViewMode}`) ||
      safeGetItem(`form_${formId}_fieldVariant__published`) ||
      safeGetItem(`form_${formId}_fieldVariant`);

    const themeData = {
      selectedTheme: selectedTheme || storedThemeSelected || 'Paper',
      colors: colors || storedThemeColors || {},
      fonts: fonts || storedThemeFonts || {},
      submitButtonStyle: submitButtonStyle || storedThemeButton || {},
      fieldVariant: fieldVariant || storedThemeVariant || 'outlined',
      lastUpdated: new Date().toISOString(),
    };


    console.log('[FormSaveContext] Theme data before compression:', themeData);

    // Fetch field metadata for saving
    // Prefer canonical in-memory context (already mode-aware in FieldMetadataContext)
    // Fallback to mode-aware storage key only if context isn't ready
    const storedFieldsMetadata = (() => {
      const fromStorage = safeGetItem(`form_${formId}_fieldsMetadata__${effectiveViewMode}`) || null;

      // If we are in builder and not viewing published, storage is the most explicit source of truth.
      if (isBuilderRoute && effectiveViewMode !== 'published') {
        return fromStorage || {};
      }

      // Otherwise prefer in-memory context (fast path), fallback to storage.
const normalizedFieldsMetadataFromCtx = fieldsMetadataFromCtx?.value || fieldsMetadataFromCtx || {};
if (normalizedFieldsMetadataFromCtx && typeof normalizedFieldsMetadataFromCtx === 'object' && Object.keys(normalizedFieldsMetadataFromCtx).length > 0) {
  return normalizedFieldsMetadataFromCtx;
}


      return fromStorage || {};
    })();

    console.log('[FormSaveContext] Retrieved field metadata for saving:', storedFieldsMetadata);

    // ─────────────────────────────────────────────────────────────
    // Flush pending Form Lists V2 spreadsheet edits before reading
    // dropdown options for compression.
    //
    // WHY
    //  • FormListsLab keeps hot edits local for spreadsheet speed.
    //  • Save Draft must still capture those edits.
    //  • The flusher writes only dirty option sets and returns an override
    //    snapshot so this save run does not depend on React context timing.
    //
    // IMPORTANT
    //  • This is NOT a full-canvas flush.
    //  • The registered FormListsLab flusher builds a dirty-only apply plan.
    // ─────────────────────────────────────────────────────────────
    let formListsDropdownOptionsOverride = null;

    try {
      if (typeof flushFormListsDraftChanges === 'function') {
        const formListsFlushResult = await flushFormListsDraftChanges({
          formId,
          reason: 'save_draft',
        });

        formListsDropdownOptionsOverride =
          formListsFlushResult?.dropdownOptionsOverride || null;

        console.log('[FormSaveContext] Form Lists V2 flush before save:', {
          status: formListsFlushResult?.status || null,
          reason: formListsFlushResult?.reason || null,
          hasDropdownOptionsOverride:
            !!formListsDropdownOptionsOverride &&
            Object.keys(formListsDropdownOptionsOverride || {}).length > 0,
          resultCount: Array.isArray(formListsFlushResult?.results)
            ? formListsFlushResult.results.length
            : 0,
        });
      }
    } catch (flushError) {
      console.error('[FormSaveContext] Failed to flush Form Lists V2 edits before save:', flushError);
      return;
    }

    // Fetch and compress dropdown options
    // Prefer:
    //  1. Form Lists V2 flush override from this save run
    //  2. canonical in-memory context (MultiAnswerFieldContext)
    //  3. storage fallback
    const storedDropdownOptions =
      formListsDropdownOptionsOverride &&
      typeof formListsDropdownOptionsOverride === 'object' &&
      Object.keys(formListsDropdownOptionsOverride).length > 0
        ? formListsDropdownOptionsOverride
        : (
            (dropdownOptionsFromCtx && typeof dropdownOptionsFromCtx === 'object' && Object.keys(dropdownOptionsFromCtx).length > 0)
              ? dropdownOptionsFromCtx
              : (safeGetItem(`form_${formId}_dropdownOptions`) || {})
          );

    console.log('[FormSaveContext] Retrieved dropdown options for saving:', storedDropdownOptions);

    const rawStoredDropdownDependencies =
      safeGetItem(`form_${formId}_dropdownDependencies__${effectiveViewMode}`) ||
      safeGetItem(`form_${formId}_dropdownDependencies`) ||
      dropdownDependencies ||
      {};

    /**
     * Clean dropdown dependencies at the Save Draft boundary.
     *
     * WHY
     *  • Spreadsheet clearing is local and reversible until Save/Publish.
     *  • DropdownDependenciesContext no longer performs destructive cleanup
     *    during normal dropdownOptions changes.
     *  • Save Draft is an intentional persistence boundary, so this is the
     *    correct time to remove dependencies for options that are no longer
     *    present in the dropdownOptions snapshot being saved.
     *
     * IMPORTANT
     *  • This does NOT change the dependency schema.
     *  • It only removes impossible/stale references before compression.
     */
    const {
      dropdownDependencies: storedDropdownDependencies,
      diagnostics: dropdownDependencyCleanupDiagnostics,
    } = cleanDropdownDependenciesForOptions({
      dropdownOptions: storedDropdownOptions,
      dropdownDependencies: rawStoredDropdownDependencies,
      preserveEmptyDependencyEntries: true,
    });

    console.log('[FormSaveContext] Cleaned dropdown dependencies for saving:', {
      cleanupDiagnostics: dropdownDependencyCleanupDiagnostics,
      rawDependencySetCount: Object.keys(rawStoredDropdownDependencies || {}).length,
      cleanedDependencySetCount: Object.keys(storedDropdownDependencies || {}).length,
    });

    console.log('[FormSaveContext] Retrieved field metadata, dropdown options, and dependencies.');

const storedSubmissionSettings =
  (submissionSettings && typeof submissionSettings === 'object' && Object.keys(submissionSettings).length > 0)
    ? submissionSettings
    : (
        safeGetItem(`submissionSettings_${formId}__${effectiveViewMode}`) || // (optional future key)
        safeGetItem(`submissionSettings_${formId}`) ||
        {}
      );

console.log('[FormSaveContext] Retrieved submission settings for saving:', storedSubmissionSettings);

//    const storedSubmitStatus = safeGetItem(`form_${formId}_submitStatus`) || submitStatus || {};
//    console.log('[FormSaveContext] Retrieved form actions (submission status) for saving:', storedSubmitStatus);

    // Serialize layoutConfig before compression
    let serializedLayoutConfig;
    try {
      // Safety fallback: if FormLayoutContext hasn't hydrated yet, fall back to mode-aware storage.
      const layoutSource =
        (layoutConfig && typeof layoutConfig === 'object' && Object.keys(layoutConfig).length > 0)
          ? layoutConfig
          : (safeGetItem(`form_${formId}_layoutConfig__${effectiveViewMode}`) ||
             safeGetItem(`form_${formId}_layoutConfig__published`) ||
             {});

      serializedLayoutConfig = JSON.parse(serializeLayoutConfig(layoutSource));
      console.log('[FormSaveContext] Serialized LayoutConfig:', serializedLayoutConfig);
    } catch (serializationError) {
      console.error('[FormSaveContext] Error serializing layoutConfig:', serializationError);
      return;
    }


// Compress data before saving
    let compressedLayoutConfig, compressedThemeData, compressedFieldsMetadata, compressedDropdownOptions, compressedDropdownDependencies, compressedSubmissionSettings;
    try {
      compressedLayoutConfig = await compressDataForDraft(serializedLayoutConfig);
      compressedThemeData = await compressDataForDraft(themeData);
      compressedFieldsMetadata = await compressDataForDraft(storedFieldsMetadata);
      compressedDropdownOptions = await compressDataForDraft(storedDropdownOptions);
      compressedDropdownDependencies = await compressDataForDraft(storedDropdownDependencies);
      compressedSubmissionSettings = await compressDataForDraft(storedSubmissionSettings);
//      compressedSubmitStatus = await compressDataForDraft(storedSubmitStatus); 

      console.log('[FormSaveContext] Compressed data:', {
        compressedLayoutConfig,
        compressedThemeData,
        compressedFieldsMetadata,
        compressedDropdownOptions,
        compressedDropdownDependencies,
        compressedSubmissionSettings,
//        compressedSubmitStatus,
      });
    } catch (compressionError) {
      console.error('[FormSaveContext] Error compressing data:', compressionError);
      return;
    }

    // Prepare the final draft data
    const draftData = {
      formId,
      formDetails: updatedFormDetails,
      layoutConfig: compressedLayoutConfig,
      fieldsMetadata: compressedFieldsMetadata,
      dropdownOptions: compressedDropdownOptions,
      dropdownDependencies: compressedDropdownDependencies,
      submissionSettings: compressedSubmissionSettings,
//      submitStatus: compressedSubmitStatus,
      // fieldChanges,
      theme: compressedThemeData, 
      status: 'draft',
    };

    console.log('[FormSaveContext] Final draftData before Firestore call:', draftData);

    if (!draftData.formDetails.title || !draftData.formDetails.description) {
      console.error('[FormSaveContext] CRITICAL: draftData.formDetails is missing title or description right before saving!');
    }

    // Log the final draftData structure
//    console.log('[FormSaveContext] Final draftData object before compression:', draftData);
//    console.log('[FormSaveContext] Final draftData with organizationId:', draftData.formDetails.organizationId);


  // Compress entire draftData (WITHOUT formDetails)
  let compressedDraftData;
  try {
    compressedDraftData = await compressDataForDraft({
      formId,
      layoutConfig: compressedLayoutConfig,
      theme: compressedThemeData,
      fieldsMetadata: compressedFieldsMetadata, 
      dropdownOptions: compressedDropdownOptions, 
      dropdownDependencies: compressedDropdownDependencies,
      submissionSettings: compressedSubmissionSettings,
//      submitStatus: compressedSubmitStatus, 
      status: 'draft',
    });
    console.log('[FormSaveContext] Compressed Draft Data:', compressedDraftData);
  } catch (compressionError) {
    console.error('[FormSaveContext] Error compressing draft data:', compressionError);
    return;
  }

  //  Save compressed data to Firestore
  try {
    console.log('[FormSaveContext] Saving draft form to Firestore with:', {
      formId,
      userId: currentUser.uid,
      organizationId,
      compressedDraftData,
      compressedLayoutConfig,
      compressedThemeData,
      compressedFieldsMetadata, 
      compressedDropdownOptions, 
      compressedDropdownDependencies,
      compressedSubmissionSettings,
  //    compressedSubmitStatus,
    });

    // enterprise “working copy” behavior
    // Always request draftOverlay. Firestore will only store an overlay if the existing form is published.
    // For non-published forms, your Firestore function will fall back to legacy draft behavior.
    console.log('[FormSaveContext] Draft saveMode:', 'draftOverlay');

    const saveResponse = await saveDraftFormToFirestore({
      formId,
      compressedDraftData, // Compressed entire form data
      compressedLayoutConfig, // Compressed layout config
      compressedThemeData, // Compressed theme data
      compressedFieldsMetadata, // Compressed field metadata
      compressedDropdownOptions, 
      compressedDropdownDependencies,
      compressedSubmissionSettings,
  //    compressedSubmitStatus, 
      userId: currentUser.uid,
      organizationId,
      formDetails: updatedFormDetails,
      saveMode: 'draftOverlay',
 // attach mapping only when present; utils/firestore merges it
 ...(storedAssetIngestConfig && typeof storedAssetIngestConfig === 'object'
   ? { assetIngestConfig: storedAssetIngestConfig }
   : {}),
    });

if (!draftData.formDetails || !draftData.formDetails.title || !draftData.formDetails.description) {
  console.error('[FormSaveContext] FINAL CHECK ERROR: formDetails is still missing title or description before saving to Firestore!');
}

      if (saveResponse?.status === 'success') {
        try {
          const integrationSyncResult =
            await syncStagedFormIntegrationConfigs({
              formId,
              organizationId,
              viewMode: effectiveViewMode,
            });

          console.log(
            '[FormSaveContext] Staged integration configs sync result:',
            integrationSyncResult
          );
        } catch (integrationSyncError) {
          console.warn(
            '[FormSaveContext] Staged integration config sync failed ' +
              '(non-fatal):',
            integrationSyncError
          );
        }

        try {
          if (typeof clearFormListsUnsavedWorkingCopy === 'function') {
            clearFormListsUnsavedWorkingCopy({
              formId,
              reason: 'save_draft_success',
            });
          }
        } catch (formListsDirtyClearError) {
          console.warn(
            '[FormSaveContext] Failed to clear Form Lists dirty state ' +
              'after successful draft save (non-fatal):',
            formListsDirtyClearError
          );
        }

        await recordSaveOnboardingEvent({
          currentUser,
          saveMode: 'draftOverlay',
          saveResponse,
        });
      } else {
        console.warn(
          '[FormSaveContext] Draft save did not return success; ' +
          'skipping onboarding save event.',
          saveResponse
        );
      }

      console.log(`[FormSaveContext] Form ${formId} saved as draft successfully with compression.`);
    } catch (error) {
      console.error(`Error saving draft form ${formId} with compression:`, error);
    }
  };

  return (
    <FormSaveContext.Provider value={{ saveFormAsDraft }}>
      {children}
    </FormSaveContext.Provider>
  );
};

export const useFormSaveContext = () => useContext(FormSaveContext);

